"use client";

import { styled } from "styled-components";

import {
  ptSerif_body_sm_bold,
  ptSerif_body_sm_regular,
} from "@/shared/font/pt-serif/body";

import type { EllipsisTooltipAnchorRect } from "./EllipsisTooltip";

export const Wrapper = styled.div`
  position: relative;
  display: block;
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-width: 0;
`;

export const TooltipContainer = styled.div<{
  $position: "left" | "right";
  $verticalPosition: "top" | "bottom";
  $anchorRect?: EllipsisTooltipAnchorRect | null;
  $maxWidth: string;
  $sameWidthAsText?: boolean;
}>`
  position: ${({ $anchorRect }) => ($anchorRect ? "fixed" : "absolute")};
  ${({ $anchorRect, $verticalPosition }) => {
    if ($anchorRect) {
      const top =
        $verticalPosition === "top"
          ? $anchorRect.top - 4
          : $anchorRect.bottom + 4;
      return `top: ${top}px;`;
    }

    return $verticalPosition === "top"
      ? "bottom: calc(100% + 0.4rem);"
      : "top: calc(100% + 0.4rem);";
  }}
  ${({ $anchorRect, $position }) => {
    if ($anchorRect) {
      return $position === "left"
        ? `left: ${$anchorRect.left}px;`
        : `right: calc(100vw - ${$anchorRect.right}px);`;
    }

    return $position === "left" ? "left: 0;" : "right: 0;";
  }}
  transform: ${({ $anchorRect, $verticalPosition }) =>
    $anchorRect && $verticalPosition === "top" ? "translateY(-100%)" : "none"};
  width: ${({ $anchorRect, $sameWidthAsText }) => {
    if (!$sameWidthAsText) return "max-content";
    return $anchorRect ? `${$anchorRect.width}px` : "100%";
  }};
  max-width: ${({ $anchorRect, $position, $sameWidthAsText, $maxWidth }) => {
    if ($sameWidthAsText) {
      return $anchorRect ? `${$anchorRect.width}px` : "100%";
    }

    if ($anchorRect) {
      const availableWidth =
        $position === "left"
          ? `calc(100vw - ${$anchorRect.left}px - 0.8rem)`
          : `calc(${$anchorRect.right}px - 0.8rem)`;

      return `min(${$maxWidth}, ${availableWidth})`;
    }

    return $maxWidth;
  }};
  z-index: 1000;
`;

export const TooltipBase = styled.div`
  display: flex;
  padding: var(--Spacing-4, 0.4rem) var(--Spacing-8, 0.8rem)
    var(--Spacing-4, 0.4rem) var(--Spacing-4, 0.4rem);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--Spacing-8, 0.8rem);

  border-radius: var(--Border-Radius-4, 0.4rem);
  border: 1px solid var(--Color-Common-Border-Tertiary, #e5e7eb);
  background: var(--Color-Common-Bg-Primary, #fff);
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16),
    0px 0px 2px 0px rgba(0, 0, 0, 0.12);

  color: var(--Color-Common-Text-Primary, #2a2f37);
  ${ptSerif_body_sm_regular}
`;

export const TooltipClamp = styled.div<{ $isBold?: boolean }>`
  ${({ $isBold }) => ($isBold ? ptSerif_body_sm_bold : ptSerif_body_sm_regular)}
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 10;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;
